import React from 'react';
import styled from 'styled-components';
import HomeCollectionIcon from '../../images/upsell/HomeCollection.svg';

const Card = styled.div`
  min-width: ${(props) => (props.cartTestCount > 1 ? '330px' : '95%')};
  max-width: ${(props) => (props.cartTestCount > 1 ? '330px' : '95%')};
  background: #fff;
  border-radius: 16px;
  box-shadow: 0px 1px 10px 0px rgba(0, 0, 0, 0.08);
  overflow: hidden;
  scroll-snap-align: center;
  -webkit-tap-highlight-color: transparent;
  -webkit-touch-callout: none;
  -webkit-user-select: none;
  user-select: none;

  &,
  & * {
    -webkit-tap-highlight-color: transparent;
  }
`;

const CardHeader = styled.div`
  padding: 16px;
  display: flex;
  gap: 4px;
  justify-content: space-between;
  align-items: flex-start;
  background: linear-gradient(277deg, #fefbf5 -37.26%, #fdedd6 161.23%);
`;

const HeaderTextWrap = styled.div`
  flex: 1;
  min-width: 0;
`;

const PackageName = styled.p`
  font-size: 16px;
  font-weight: 700;
  color: #0f0b28;
  margin: 0 0 4px;
  line-height: 1.3;
`;

const PackageDescription = styled.p`
  font-size: 12px;
  font-weight: 400;
  color: #767787;
  margin: 0;
  line-height: 1.4;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const PackageIcon = styled.div`
  width: 32px;
  height: 32px;
  display: flex;
  justify-content: center;
  align-items: center;
  border: 0.842px solid #fae0bc;
  border-radius: 6.737px;
  background-color: #fffcf6;
`;

const CardBody = styled.div`
  padding: 32px 16px;
`;

const AdditionalTestWrapper = styled.div`
  background: #fafafa;
`;
const AdditionalTestLink = styled.div`
  width: 100%;
  display: inline-flex;
  justify-content: center;
  align-items: center;
  gap: 4px;
  font-size: 13px;
  font-weight: 600;
  color: var(--primary-button, #714fff);
  cursor: pointer;
  padding: 8px 12px;
  background: #ede8ff;
  border-radius: 8px;
  touch-action: manipulation;
`;

const TestRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 6px;
`;

const TestName = styled.span`
  font-size: 11px;
  color: #121217;
  font-weight: 500;
  line-height: 150%;
  &::before {
    content: '•  ';
    color: #121217;
  }
`;

const TestCount = styled.span`
  font-size: 12px;
  color: #686670;
  font-weight: 500;
  white-space: nowrap;
`;

const DashedDivider = styled.div`
  border-top: 1px dashed #d8d7db;
  margin: 8px 0;
`;

const HomeCollectionRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const HomeCollectionLabel = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  font-weight: 500;
  color: #100e15;
  line-height: 150%;
`;

const HomeCollectionIconImg = styled.img`
  width: 18px;
  height: 18px;
`;

const PriceWrap = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;

const OfferPrice = styled.span`
  font-size: 18px;
  font-weight: 700;
  color: #100e15;
  line-height: 24px;
  letter-spacing: -0.18px;
`;

const MrpPrice = styled.span`
  font-size: 13px;
  font-weight: 500;
  color: #767787;
  text-decoration: line-through;
`;

const CardFooter = styled.div`
  margin: 12px 0 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  background: #dcfcea;
  border-radius: 16px;
`;

const UpgradeButton = styled.button`
  width: 100%;
  margin-top: -2px;
  padding: 12px;
  border-radius: 12px;
  border: 2px solid var(--primary-button, #714fff);
  background: #ffffff;
  color: var(--primary-button, #714fff);
  cursor: pointer;
  text-align: center;
  font-size: 14px;
  font-weight: 600;
  line-height: 150%;
  -webkit-tap-highlight-color: transparent;
  touch-action: manipulation;
`;

const FreeCollectionText = styled.p`
  font-size: 11px;
  font-weight: 700;
  color: #1eb865;
  margin: 3px 0 4px;
  line-height: 13.5px;
  letter-spacing: 0.167px;
`;

const formatPrice = (val) => {
  const num = Number(val);
  if (isNaN(num)) return val;
  return num.toLocaleString('en-IN');
};

const RecommendedPackageCard = ({
  name,
  partnerImage,
  description,
  additionalTestCount,
  tests,
  offerPrice,
  mrp,
  upgradeExtraCost,
  freeHomeCollection,
  onUpgrade,
  onViewTests,
  cartTestCount,
}) => {
  return (
    <Card cartTestCount={cartTestCount}>
      <CardHeader>
        <HeaderTextWrap>
          <PackageName>{name}</PackageName>
          <PackageDescription>{description}</PackageDescription>
        </HeaderTextWrap>
        <PackageIcon>
          <img
            src={partnerImage}
            alt="upgrade"
            style={{ width: '24px', height: '24px' }}
          />
        </PackageIcon>
      </CardHeader>

      <CardBody>
        {additionalTestCount > 0 && tests && tests.length > 0 && (
          <AdditionalTestWrapper>
            <AdditionalTestLink onClick={onViewTests}>
              {additionalTestCount} Additional Test &rsaquo;
            </AdditionalTestLink>

            {tests.map((test, idx) => (
              <TestRow key={idx}>
                <TestName>{test.name}</TestName>
                <TestCount>{test.count} Test</TestCount>
              </TestRow>
            ))}
          </AdditionalTestWrapper>
        )}

        <DashedDivider />

        <HomeCollectionRow>
          <HomeCollectionLabel>
            <HomeCollectionIconImg src={HomeCollectionIcon} alt="home" />
            Home Collection
          </HomeCollectionLabel>
          <PriceWrap>
            <OfferPrice>₹{formatPrice(offerPrice)}</OfferPrice>
            {mrp && Number(mrp) !== Number(offerPrice) && (
              <MrpPrice>₹{formatPrice(mrp)}</MrpPrice>
            )}
          </PriceWrap>
        </HomeCollectionRow>
        <CardFooter>
          <UpgradeButton type="button" onClick={onUpgrade}>
            Upgrade for ₹{formatPrice(upgradeExtraCost)} extra
          </UpgradeButton>
          {freeHomeCollection && (
            <FreeCollectionText>Free Home collection</FreeCollectionText>
          )}
        </CardFooter>
      </CardBody>
    </Card>
  );
};

export default RecommendedPackageCard;
